import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { confirmAction, copyText, haptic, notify, openTelegram, useBackButton } from '../telegram'
import type { DeskBooking, DeskOut } from '../types'
import { euro } from './Booking'

interface Props {
  clubId: number
  onClose: () => void
}

function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString(t().locale, { hour: '2-digit', minute: '2-digit' })
}

function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${t().weekdays[d.getDay()]} ${d.toLocaleDateString(t().locale, { day: '2-digit', month: '2-digit' })}`
}

/** Кабинет клуба (v0.35): заявки с кнопками, расписание по дням, отмена,
 *  неявка, настройки и ссылка для игроков. Те же действия, что кнопками в боте. */
export default function ClubDesk({ clubId, onClose }: Props) {
  const [data, setData] = useState<DeskOut | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  useEffect(() => useBackButton(onClose), [onClose])
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setData(await api.clubDesk(clubId))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [clubId])

  useEffect(() => {
    void load()
    const id = window.setInterval(() => void load(), 20000)
    return () => window.clearInterval(id)
  }, [load])

  async function act(item: DeskBooking, fn: () => Promise<DeskBooking>, ask?: string) {
    if (ask && !(await confirmAction(ask))) return
    setBusy(item.id)
    try {
      const updated = await fn()
      haptic('medium')
      setData((d) => (d ? { ...d, items: d.items.map((x) => (x.id === updated.id ? updated : x)) } : d))
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
      void load()
    } finally {
      setBusy(null)
    }
  }

  const now = Date.now()
  const items = data?.items ?? []
  const fresh = items.filter((i) => i.can_answer)
  const schedule = items.filter((i) => i.status === 'confirmed' && new Date(i.ends_at).getTime() > now - 3 * 3600000)
  const other = items.filter((i) => !fresh.includes(i) && !schedule.includes(i)).reverse()
  const byDay: [string, DeskBooking[]][] = []
  for (const item of schedule) {
    const key = dayKey(item.starts_at)
    const last = byDay[byDay.length - 1]
    if (last && last[0] === key) last[1].push(item)
    else byDay.push([key, [item]])
  }

  const club = data?.club
  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={club ? t().deskTitle(club.name) : ''}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">📋 {club ? t().deskTitle(club.name) : t().loading}</div>
      </div>
      <div className="chat-list">
        {error && !data && <div className="card muted">{error}</div>}
        {!data && !error && <p className="muted">{t().loading}</p>}
        {data && club && (
          <>
            <div className="day-head">{t().deskNew(fresh.length)}</div>
            {fresh.length === 0 && <div className="card muted">{t().deskNothingNew}</div>}
            {fresh.map((item) => (
              <DeskCard key={item.id} item={item} highlight>
                <div className="row" style={{ gap: 8, marginTop: 10 }}>
                  <button style={{ flex: 1 }} disabled={busy === item.id} onClick={() => act(item, () => api.deskAnswer(item.id, true))}>
                    ✅ {t().deskConfirm}
                  </button>
                  <button className="ghost" style={{ flex: 1 }} disabled={busy === item.id} onClick={() => act(item, () => api.deskAnswer(item.id, false))}>
                    {t().deskDecline}
                  </button>
                </div>
              </DeskCard>
            ))}

            <div className="day-head">{t().deskSchedule}</div>
            {byDay.length === 0 && <div className="card muted">{t().deskEmpty}</div>}
            {byDay.map(([day, list]) => (
              <div key={day}>
                <div className="desk-day">{day}</div>
                {list.map((item) => (
                  <DeskCard key={item.id} item={item}>
                    {(item.can_club_cancel || item.can_no_show) && (
                      <div className="row" style={{ gap: 8, marginTop: 8 }}>
                        {item.can_no_show && (
                          <button className="ghost" style={{ flex: 1 }} disabled={busy === item.id} onClick={() => act(item, () => api.deskNoShow(item.id, !item.no_show))}>
                            {item.no_show ? t().deskNoShowUndo : t().deskNoShow}
                          </button>
                        )}
                        {item.can_club_cancel && new Date(item.starts_at).getTime() > now && (
                          <button className="ghost" style={{ flex: 1 }} disabled={busy === item.id} onClick={() => act(item, () => api.deskCancel(item.id), t().deskCancelAsk)}>
                            {t().deskCancel}
                          </button>
                        )}
                      </div>
                    )}
                  </DeskCard>
                ))}
              </div>
            ))}

            {other.length > 0 && (
              <details className="desk-past">
                <summary className="day-head">{t().deskPast} · {other.length}</summary>
                {other.map((item) => (
                  <DeskCard key={item.id} item={item} />
                ))}
              </details>
            )}

            <div className="day-head">{t().deskLink}</div>
            <div className="card">
              <div className="muted small">{t().deskLinkHint}</div>
              <div className="desk-link">{data.link_web}</div>
              <button
                className="ghost"
                style={{ width: '100%', marginTop: 8 }}
                onClick={async () => {
                  if (await copyText(data.link_web)) notify(t().bookCopied)
                }}
              >
                {t().deskCopyLink}
              </button>
              {!club.public && <div className="muted small" style={{ marginTop: 8 }}>{t().deskHidden}</div>}
            </div>

            <div className="day-head">{t().deskSettings}</div>
            <div className="card desk-settings">
              <div>{t().deskHours(club.open, club.close, club.step)}</div>
              <div>
                {club.prices
                  .filter((p) => p.amount != null)
                  .map((p) => `${t().bookHours(p.minutes)} — ${euro(p.amount)}`)
                  .join(' · ')}
                {club.price_note ? ` (${club.price_note})` : ''}
              </div>
              <div>{club.courts.join(' · ')}</div>
              <div>{t().deskCancelRule(club.cancel_hours)}</div>
              <div>{t().deskReplyRule(club.reply_hours)}</div>
              {data.email && <div>{t().deskEmail(data.email)}</div>}
              <div style={{ marginTop: 8 }}>
                {data.calendar.enabled ? (
                  <>
                    ✅ {t().deskCalendarOn}
                    {data.calendar.synced_at && <span className="muted"> · {t().deskCalendarSynced(formatWhen(data.calendar.synced_at))}</span>}
                  </>
                ) : (
                  <>
                    <div>📅 {t().deskCalendarOff}</div>
                    <div className="muted small" style={{ marginTop: 4 }}>
                      {data.calendar.service_email ? t().deskCalendarHow(data.calendar.service_email) : t().deskCalendarNoSa}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function DeskCard({ item, highlight, children }: { item: DeskBooking; highlight?: boolean; children?: ReactNode }) {
  const done = item.status !== 'requested' && item.status !== 'confirmed'
  return (
    <div className={`card desk-card${highlight ? ' fresh' : ''}${done ? ' past' : ''}`}>
      <div className="row between" style={{ gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="title">
            {timeOnly(item.starts_at)}–{timeOnly(item.ends_at)} · {item.court_name}
          </div>
          <div className="muted small">{formatWhen(item.starts_at).split(',')[0]}{item.amount != null && ` · ${euro(item.amount)}`}</div>
        </div>
        {!highlight && <span className={`badge${item.status === 'confirmed' ? '' : ' neutral'}`}>{t().bookStatus[item.status]}</span>}
      </div>
      <div style={{ marginTop: 6 }}>
        👤{' '}
        {item.player_username ? (
          <button className="link" onClick={() => openTelegram(`https://t.me/${item.player_username}`)}>
            {item.player_name} (@{item.player_username})
          </button>
        ) : (
          <b>{item.player_name}</b>
        )}
        <span className="muted small"> · {t().deskLevel(item.player_level != null ? item.player_level.toFixed(2) : '—', item.player_reliability)}</span>
      </div>
      {item.note && <div style={{ marginTop: 4 }}>💬 {item.note}</div>}
      {highlight && item.reply_by && <div className="muted small" style={{ marginTop: 4 }}>⏳ {t().deskReply(formatWhen(item.reply_by))}</div>}
      {(item.late_cancel || item.no_show || item.cancelled_by) && (
        <div className="small book-late" style={{ marginTop: 4 }}>
          {[item.cancelled_by && t().deskCancelledBy[item.cancelled_by], item.late_cancel && t().bookLate, item.no_show && t().bookNoShow].filter(Boolean).join(' · ')}
        </div>
      )}
      {item.calendar_state === 'error' && <div className="small book-late" style={{ marginTop: 4 }}>📅 {t().deskCalendarError}</div>}
      {children}
    </div>
  )
}
